import Cookies from "js-cookie";
import React, { FC, useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import AppShellLayout from "../layouts/AppShellLayout";
import BaseLayout from "../layouts/BaseLayout";
import GlobalAdvertiserInit from "../components/GlobalAdvertiserInit";
import Loader from "../components/elements/Loader";
import { ReduxApplicationState } from "../types/ReduxTypes/ReduxApplicationState";
import { SentryRoute } from "../sentry";
import { ensureAgencyForUser } from "../utils/ensureAgencyForUser";
import { setAgency, setBrandgenPlayBooks } from "../redux/reducers/agency";
import { setSession } from "../redux/reducers/session";
import { setUser } from "../redux/reducers/user";

type AuthGuardRouteProps = {
  exact?: boolean;
  path: string;
  component: React.ComponentType<any>;
  useNewLayout?: boolean;
  title?: string;
  subtitle?: string;
};

const fetchAuthenticatedUser = async () => {
  const authenticatedUser = await Auth.currentAuthenticatedUser();
  return authenticatedUser;
};

const AuthGuardRoute: FC<AuthGuardRouteProps> = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  component: Component,
  useNewLayout,
  title,
  subtitle,
  ...rest
}) => {
  const dispatch = useDispatch();

  const isAuthenticated: boolean = useSelector((state: ReduxApplicationState) => state.session.isAuthenticated);
  const agency = useSelector((state: ReduxApplicationState) => state.agency);
  const session = useSelector((state: ReduxApplicationState) => state.session.session);

  const [isUserAuthenticatedChecking, setIsUserAuthenticatedChecking] = useState<boolean>(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setIsUserAuthenticatedChecking(true);
        const user = await Auth.currentSession();

        if (user) {
          const newSession = {
            accessToken: user.getAccessToken().getJwtToken(),
            idToken: user.getIdToken().getJwtToken(),
            refreshToken: user.getRefreshToken().getToken(),
          };
          if (!session) {
            await dispatch(setSession(newSession));
          }
          Cookies.set("ACCESS_TOKEN", newSession.accessToken);
        }

        if ((session || user) && !agency.name) {
          const authenticatedUser = await fetchAuthenticatedUser();
          const associatedAgency = await ensureAgencyForUser(authenticatedUser);
          if (associatedAgency) {
            dispatch(setAgency(associatedAgency));
          }
          dispatch(setUser(authenticatedUser.username));
          dispatch(setBrandgenPlayBooks());
          setIsUserAuthenticatedChecking(false);
        } else {
          setIsUserAuthenticatedChecking(false);
        }
      } catch (error) {
        if (error === "No current user") {
          setIsUserAuthenticatedChecking(false);
          // eslint-disable-next-line no-console
          console.log("User is not Authenticated!");
        } else {
          setIsUserAuthenticatedChecking(false);
          // eslint-disable-next-line no-console
          console.log("error:", error);
        }
      }
    };
    checkAuthStatus();
  }, [agency.name, dispatch, session]);

  return (
    <SentryRoute
      render={(props) => {
        if (!isUserAuthenticatedChecking && !isAuthenticated) {
          return <Redirect to={{ pathname: "/" }} />;
        }
        const child = isUserAuthenticatedChecking ? <Loader /> : <Component {...props} />;
        const content = (
          <>
            <GlobalAdvertiserInit />
            {child}
          </>
        );
        return useNewLayout ? (
          <AppShellLayout key={rest.path as string} title={title ?? ""} subtitle={subtitle}>
            {content}
          </AppShellLayout>
        ) : (
          <BaseLayout>{content}</BaseLayout>
        );
      }}
      {...rest}
    />
  );
};

export default AuthGuardRoute;
